import React, { useState, useEffect, useCallback, memo } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { cn, getInitials } from '../lib/utils';
import {
    Users,
    Clock,
    Play,
    Loader2,
    RefreshCw,
    User,
} from 'lucide-react';

// Isolated timer component — only this re-renders per second, not the whole table
const LiveTrackingDuration = memo(({ startTime }) => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);
    if (!startTime) return <span className="font-mono text-sm font-semibold text-green-600">0m</span>;
    const diffMs = now - new Date(startTime);
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    const label = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    return <span className="font-mono text-sm font-semibold text-green-600">{label}</span>;
});

const secondsToHuman = (seconds) => {
    if (seconds < 60) return `${seconds}d`;
    const m = Math.floor(seconds / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${m % 60}m`;
    const d = Math.floor(h / 24);
    return `${d}h ${h % 24}j`;
};

const LiveIdleTime = memo(({ lastTrackedAt, idleSeconds }) => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);
    if (lastTrackedAt) {
        const seconds = Math.max(0, Math.floor((now - new Date(lastTrackedAt)) / 1000));
        return <span className="font-mono">{secondsToHuman(seconds)}</span>;
    }
    if (typeof idleSeconds === 'number') {
        return <span className="font-mono">{secondsToHuman(idleSeconds)}</span>;
    }
    return <span>—</span>;
});
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { TaskDetailFullModal } from '../components/TaskDetailFullModal';
import SpaceTasksSlideOver from '../components/SpaceTasksSlideOver';


export default function TrackingMemberPage() {
    const navigate = useNavigate();
    const { currentWorkspace, fetchWorkspaces, selectWorkspace, lists, members: workspaceMembers } = useWorkspace();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Member tasks SlideOver (flow like Performance Task Member)
    const [selectedMember, setSelectedMember] = useState(null);
    const [isMemberSlideOverOpen, setIsMemberSlideOverOpen] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);

    // Pagination for Idle list
    const [idlePage, setIdlePage] = useState(1);
    const IDLE_PAGE_SIZE = 20;
    

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedTask(null);
    };

    const fetchMemberTracking = useCallback(async () => {
        if (!currentWorkspace) return;
        try {
            const response = await api.get(`/workspaces/${currentWorkspace.id}/member-tracking`);
            setMembers(response.data);
        } catch (error) {
            console.error('Failed to fetch member tracking:', error);
        }
    }, [currentWorkspace]);

    useEffect(() => {
        initPage();
    }, []);

    useEffect(() => {
        if (currentWorkspace) {
            setLoading(true);
            fetchMemberTracking().finally(() => setLoading(false));
        }
    }, [currentWorkspace, fetchMemberTracking]);

    // Auto-refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            fetchMemberTracking();
        }, 30000);
        return () => clearInterval(interval);
    }, [fetchMemberTracking]);

    const initPage = async () => {
        if (!currentWorkspace) {
            const ws = await fetchWorkspaces();
            // if (ws.length > 0) {
            //     await selectWorkspace(ws[0]);
            // }
        }
        setLoading(false);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchMemberTracking();
        setRefreshing(false);
    };

    const openMemberTasks = (member) => {
        // Shape compatible with SpaceTasksSlideOver
        setSelectedMember({
            user_id: member.user_id,
            user_name: member.name,
            role: member.role,
        });
        setIsMemberSlideOverOpen(true);
    };

    const trackingMembers = members.filter(m => m.is_tracking);
    const idleMembers = members.filter(m => !m.is_tracking);
    
    // Reset page when filter changes
    useEffect(() => {
        setIdlePage(1);
    }, [idleMembers.length]);

    if (loading) {
        return (
            <Layout title="Tracking Member">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    if (!currentWorkspace) {
        return (
            <Layout title="Tracking Member">
                <div className="p-6 text-center text-brand-text-muted">
                    Pilih workspace untuk melihat tracking member
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Tracking Member">
            <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-brand-text-primary flex items-center gap-2">
                            <Users className="w-6 h-6 text-brand-primary" strokeWidth={1.5} />
                            Tracking Member - { members.length }
                        </h1>
                        <p className="text-brand-text-secondary mt-1">
                            Monitor aktivitas tracking waktu member
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        data-testid="refresh-tracking-btn"
                    >
                        <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-brand-primary/10 rounded-lg">
                                    <Users className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-brand-text-primary">{members.length}</p>
                                    <p className="text-sm text-brand-text-muted">Total Member</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <Play className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-green-600">{trackingMembers.length}</p>
                                    <p className="text-sm text-brand-text-muted">Sedang Tracking</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-100 rounded-lg">
                                    <Clock className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-500">{idleMembers.length}</p>
                                    <p className="text-sm text-brand-text-muted">Idle</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Currently Tracking Section - Table View */}
                {trackingMembers.length > 0 && (
                    <Card className="border-green-200 shadow-card">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Play className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                                Sedang Tracking ({trackingMembers.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-y border-gray-200">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                Nama
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                Waktu
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                Task
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {trackingMembers.map((member) => (
                                            <tr 
                                                key={member.user_id} 
                                                className="hover:bg-green-50/50 transition-colors"
                                                data-testid={`tracking-row-${member.user_id}`}
                                            >
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-brand-primary text-white flex items-center justify-center font-semibold text-xs flex-shrink-0">
                                                            {getInitials(member.name)}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-brand-text-primary">
                                                                {member.name}
                                                            </p>
                                                            <p className="text-xs text-brand-text-muted">
                                                                {member.email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                                        <LiveTrackingDuration startTime={member.tracking?.start_time} />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {member.tracking && (
                                                        <div 
                                                            className="cursor-pointer hover:text-brand-primary"
                                                            onClick={() => navigate(`/tasks?task=${member.tracking.task_id}`)}
                                                        >
                                                            <span className="font-mono text-xs text-brand-primary font-semibold">
                                                                {member.tracking.task_code || 'TASK'}
                                                            </span>
                                                            <span className="text-gray-400 mx-2">-</span>
                                                            <span className="text-sm text-brand-text-primary">
                                                                {member.tracking.task_title}
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* No One Tracking Message */}
                {trackingMembers.length === 0 && members.length > 0 && (
                    <Card className="border-brand-border">
                        <CardContent className="p-8 text-center">
                            <Clock className="w-12 h-12 mx-auto text-brand-text-muted mb-3" strokeWidth={1.5} />
                            <p className="text-brand-text-muted">Tidak ada member yang sedang tracking saat ini</p>
                        </CardContent>
                    </Card>
                )}

                {/* Idle Members Section */}
                <Card className="border-brand-border shadow-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <User className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                            Idle ({idleMembers.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {idleMembers.length === 0 ? (
                            <div className="p-8 text-center text-brand-text-muted">
                                <Users className="w-12 h-12 mx-auto mb-3" strokeWidth={1.5} />
                                Semua member sedang tracking
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-y border-gray-200">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Nama</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Waktu</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Jumlah To Do</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {idleMembers
                                                .slice((idlePage - 1) * IDLE_PAGE_SIZE, idlePage * IDLE_PAGE_SIZE)
                                                .map((member) => (
                                                    <tr
                                                        key={member.user_id}
                                                        className="hover:bg-brand-accent-soft transition-colors cursor-pointer"
                                                        data-testid={`idle-row-${member.user_id}`}
                                                        onClick={() => openMemberTasks(member)}
                                                    >
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold text-xs flex-shrink-0">
                                                                    {getInitials(member.name)}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="font-medium text-brand-text-primary truncate">{member.name}</p>
                                                                    <p className="text-xs text-brand-text-muted truncate capitalize">{member.role}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                                <Clock className="w-3.5 h-3.5" />
                                                                <LiveIdleTime lastTrackedAt={member.last_tracked_at} idleSeconds={member.idle_seconds} />
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {member.todo_count > 0 ? (
                                                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">
                                                                    {member.todo_count} To Do
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-brand-text-muted">0</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {idleMembers.length > IDLE_PAGE_SIZE && (
                                    <div className="flex items-center justify-between px-4 py-3 border-t border-brand-border">
                                        <div className="text-sm text-brand-text-muted" data-testid="idle-pagination-info">
                                            Halaman {idlePage} dari {Math.ceil(idleMembers.length / IDLE_PAGE_SIZE)} — {idleMembers.length} member
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setIdlePage(p => Math.max(1, p - 1))}
                                                disabled={idlePage === 1}
                                                className="px-3 py-1 text-sm border border-brand-border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                data-testid="idle-prev-btn"
                                            >Sebelumnya</button>
                                            <button
                                                onClick={() => setIdlePage(p => Math.min(Math.ceil(idleMembers.length / IDLE_PAGE_SIZE), p + 1))}
                                                disabled={idlePage >= Math.ceil(idleMembers.length / IDLE_PAGE_SIZE)}
                                                className="px-3 py-1 text-sm border border-brand-border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                data-testid="idle-next-btn"
                                            >Selanjutnya</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
             {/* Task Detail Full Modal */}
                {selectedTask && isModalOpen && (
                    <TaskDetailFullModal
                        isOpen={isModalOpen}
                        onClose={handleModalClose}
                        task={selectedTask}
                        listId={selectedTask?.list_id}
                        statuses={[]}
                        members={workspaceMembers}
                        onTaskUpdate={() => {}}
                    />
                )}
            
            {/* Member Tasks SlideOver — same flow as "Performance Task Member" */}
            {selectedMember && (
                <SpaceTasksSlideOver
                    isOpen={isMemberSlideOverOpen}
                    onClose={() => {
                        setIsMemberSlideOverOpen(false);
                        setSelectedMember(null);
                    }}
                    space={{
                        id: `member-${selectedMember.user_id}`,
                        name: `Tasks - ${selectedMember.user_name}`,
                        color: '#2F6F63'
                    }}
                    members={workspaceMembers}
                    filterByMember={selectedMember.user_id}
                />
            )}
        </Layout>
    );
}
